import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, CheckCircle, Circle, Clock } from '@phosphor-icons/react';

interface ProgressTrackerProps {
  selectedDatasetCount: number;
  hasQualityReports: boolean;
  hasCorrelations: boolean;
  hasVisualizations: boolean;
  hasExports: boolean;
  currentTab: string;
  onStepClick: (step: string) => void;
}

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'available' | 'locked';
  tab: string;
  estimatedTime: string;
}

export function ProgressTracker({
  selectedDatasetCount,
  hasQualityReports,
  hasCorrelations,
  hasVisualizations,
  hasExports,
  currentTab,
  onStepClick,
}: ProgressTrackerProps) {
  const [overallProgress, setOverallProgress] = useState(0);

  const getSteps = (): WorkflowStep[] => {
    return [
      {
        id: 'start',
        title: 'Choose Starting Point',
        description: 'Select a template or browse data sources',
        status: 'completed',
        tab: 'templates',
        estimatedTime: '2 min',
      },
      {
        id: 'select',
        title: 'Select Datasets',
        description: 'Choose datasets for your analysis',
        status:
          selectedDatasetCount > 0
            ? 'completed'
            : currentTab === 'discover' || currentTab === 'datasets'
              ? 'current'
              : 'available',
        tab: 'discover',
        estimatedTime: '5 min',
      },
      {
        id: 'quality',
        title: 'Check Quality',
        description: 'Validate data quality and completeness',
        status: hasQualityReports
          ? 'completed'
          : selectedDatasetCount > 0
            ? currentTab === 'quality'
              ? 'current'
              : 'available'
            : 'locked',
        tab: 'quality',
        estimatedTime: '3 min',
      },
      {
        id: 'analyze',
        title: 'Analyze Data',
        description: 'Find correlations and patterns',
        status: hasCorrelations
          ? 'completed'
          : selectedDatasetCount >= 2
            ? currentTab === 'correlations'
              ? 'current'
              : 'available'
            : 'locked',
        tab: 'correlations',
        estimatedTime: '5 min',
      },
      {
        id: 'transform',
        title: 'Transform Data',
        description: 'Clean and prepare data with pipelines',
        status:
          selectedDatasetCount > 0
            ? currentTab === 'transform'
              ? 'current'
              : 'available'
            : 'locked',
        tab: 'transform',
        estimatedTime: '10 min',
      },
      {
        id: 'visualize',
        title: 'Create Visualizations',
        description: 'Build charts and graphs',
        status: hasVisualizations
          ? 'completed'
          : selectedDatasetCount > 0
            ? currentTab === 'visualize'
              ? 'current'
              : 'available'
            : 'locked',
        tab: 'visualize',
        estimatedTime: '8 min',
      },
      {
        id: 'export',
        title: 'Export Results',
        description: 'Save your work for external tools',
        status: hasExports
          ? 'completed'
          : selectedDatasetCount > 0
            ? currentTab === 'export'
              ? 'current'
              : 'available'
            : 'locked',
        tab: 'export',
        estimatedTime: '2 min',
      },
    ];
  };

  const steps = getSteps();
  const completedSteps = steps.filter(
    step => step.status === 'completed'
  ).length;
  const currentStep = steps.find(step => step.status === 'current');

  useEffect(() => {
    const newProgress = (completedSteps / steps.length) * 100;

    setOverallProgress(newProgress);
  }, [completedSteps, steps.length]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className='w-5 h-5 text-green-600' weight='fill' />;
      case 'current':
        return <Circle className='w-5 h-5 text-primary' weight='bold' />;
      case 'available':
        return <Circle className='w-5 h-5 text-muted-foreground' />;
      case 'locked':
        return <Clock className='w-5 h-5 text-muted-foreground/50' />;
      default:
        return <Circle className='w-5 h-5 text-muted-foreground' />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'current':
        return 'border-primary bg-primary/5';
      case 'available':
        return 'border-border bg-card hover:bg-accent/50';
      case 'locked':
        return 'border-border bg-muted/30';
      default:
        return 'border-border bg-card';
    }
  };

  return (
    <Card className='mb-6'>
      <CardHeader className='pb-4'>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='text-lg'>Progress Tracker</CardTitle>
            <CardDescription>
              {completedSteps} of {steps.length} steps completed
            </CardDescription>
          </div>
          <Badge variant='outline' className='text-sm'>
            {Math.round(overallProgress)}% Complete
          </Badge>
        </div>
        <Progress value={overallProgress} className='mt-2' />
      </CardHeader>
      <CardContent className='space-y-3'>
        {steps.map((step, index) => (
          <div key={step.id} className='flex items-start gap-3'>
            <div className='flex flex-col items-center'>
              {getStatusIcon(step.status)}
              {index < steps.length - 1 && (
                <div className='w-px h-8 bg-border mt-2' />
              )}
            </div>

            <div
              className={`flex-1 p-3 rounded-lg border transition-all cursor-pointer ${getStatusColor(step.status)}`}
              onClick={() => step.status !== 'locked' && onStepClick(step.tab)}
            >
              <div className='flex items-center justify-between mb-1'>
                <div className='flex items-center gap-2'>
                  <h4 className='font-medium text-sm'>{step.title}</h4>
                  {step.status === 'current' && (
                    <Badge variant='default' className='text-xs'>
                      Current
                    </Badge>
                  )}
                  {step.status === 'completed' && (
                    <Badge variant='secondary' className='text-xs'>
                      ✓ Done
                    </Badge>
                  )}
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-xs text-muted-foreground'>
                    {step.estimatedTime}
                  </span>
                  {step.status !== 'locked' && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground' />
                  )}
                </div>
              </div>
              <p className='text-xs text-muted-foreground'>
                {step.description}
              </p>
            </div>
          </div>
        ))}

        {currentStep && (
          <div className='mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20'>
            <div className='flex items-center gap-2 mb-1'>
              <Circle className='w-4 h-4 text-primary' weight='bold' />
              <span className='text-sm font-medium'>
                Next: {currentStep.title}
              </span>
            </div>
            <p className='text-xs text-muted-foreground ml-6'>
              {currentStep.description}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
